import type { ReactNode } from 'react';
import { createElement } from 'react';
import type { PromptModel } from '../../components/ui/beui';
import {
  AI_PROVIDERS,
  getConfiguredProviders,
  getCustomModels,
} from '../../services/apiKeyService';
import type { AIProvider } from '../../services/apiKeyService';

const PROVIDER_ICON: Record<AIProvider, string> = {
  gemini: '✨',
  openai: '🔮',
  anthropic: '🧠',
  custom: '🔌',
};

/** Picker-value prefix marking a specific saved custom-provider (OpenRouter) model. */
export const CUSTOM_MODEL_PREFIX = 'custom:';

export interface ModelSelection {
  provider: AIProvider;
  /** Saved custom-provider model id, when the selection points at one. */
  modelId?: string;
}

/** Encode a selection as a PromptModel value ('custom:<model id>' or a provider id). */
export function encodeModelSelection(provider: AIProvider, modelId?: string): string {
  return provider === 'custom' && modelId ? `${CUSTOM_MODEL_PREFIX}${modelId}` : provider;
}

export function decodeModelSelection(value: string): ModelSelection {
  if (value.startsWith(CUSTOM_MODEL_PREFIX)) {
    return { provider: 'custom', modelId: value.slice(CUSTOM_MODEL_PREFIX.length) };
  }
  return { provider: value as AIProvider };
}

/** 'deepseek/deepseek-chat-v3.1' → 'deepseek-chat-v3.1' (vendor dropped for compact labels). */
export function shortModelName(modelId: string): string {
  return modelId.split('/').pop() || modelId;
}

const icon = (emoji: string) =>
  createElement('span', { className: 'text-xs' }, emoji) as ReactNode;

/** Label for a custom-provider model: the model name in mono, vendor muted. */
function customModelLabel(modelId: string): ReactNode {
  const slash = modelId.indexOf('/');
  if (slash === -1) {
    return createElement('span', { className: 'font-mono' }, modelId);
  }
  return createElement(
    'span',
    null,
    createElement('span', { className: 'font-mono' }, modelId.slice(slash + 1)),
    createElement(
      'span',
      { className: 'text-muted-foreground' },
      ` · ${modelId.slice(0, slash)}`
    ),
  );
}

/**
 * Model select options for PromptInput: one entry per configured provider,
 * expanded — for the custom/OpenRouter provider — into one entry per saved
 * model, so GLM / DeepSeek / Sonnet etc. are individually selectable.
 */
export function buildProviderModels(): PromptModel[] {
  const models: PromptModel[] = [];
  for (const p of getConfiguredProviders()) {
    if (p === 'custom') {
      const saved = getCustomModels();
      if (saved.length > 0) {
        for (const id of saved) {
          models.push({
            value: encodeModelSelection('custom', id),
            label: customModelLabel(id),
            icon: icon(PROVIDER_ICON[p]),
          });
        }
        continue;
      }
      // Key saved but no models yet — still list the provider; generation
      // points at Settings with the "no model set" error.
    }
    models.push({
      value: p,
      label: AI_PROVIDERS[p].name,
      icon: icon(PROVIDER_ICON[p]),
    });
  }
  return models;
}
